import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../lib/api.js";
import type { ApiTokenRecord, MintedApiToken, TokenScope } from "../lib/types.js";

const inputCls =
  "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const btnPrimary =
  "shrink-0 rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const SCOPES: Array<{ value: TokenScope; label: string; hint: string }> = [
  { value: "integration", label: "integration", hint: "Queue, read, and export from another app." },
  { value: "worker", label: "worker", hint: "Claim and complete jobs from the local worker PC." },
];

function fmtDate(iso?: string): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
}

function statusOf(t: ApiTokenRecord): string {
  if (t.revokedAt) return "revoked";
  if (Date.parse(t.expiresAt) <= Date.now()) return "expired";
  return "active";
}

export function Tokens() {
  const [tokens, setTokens] = useState<ApiTokenRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [label, setLabel] = useState("");
  const [scope, setScope] = useState<TokenScope>("integration");
  const [minting, setMinting] = useState(false);
  const [justMinted, setJustMinted] = useState<MintedApiToken | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api<{ items: ApiTokenRecord[] }>("/api/tokens");
      setTokens(res.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load tokens.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleMint(e: FormEvent) {
    e.preventDefault();
    if (minting) return;
    setMinting(true);
    setError(null);
    setCopied(false);
    try {
      const minted = await api<MintedApiToken>("/api/tokens", {
        method: "POST",
        json: { label: label.trim(), scope },
      });
      setJustMinted(minted);
      setLabel("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not mint the token.");
    } finally {
      setMinting(false);
    }
  }

  async function handleRevoke(tokenId: string) {
    if (!confirm("Revoke this token? Any app or worker using it stops working immediately.")) return;
    setError(null);
    try {
      await api(`/api/tokens/${tokenId}`, { method: "DELETE" });
      if (justMinted?.tokenId === tokenId) setJustMinted(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke the token.");
    }
  }

  async function copyToken() {
    if (!justMinted) return;
    try {
      await navigator.clipboard.writeText(justMinted.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">API tokens</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Bearer tokens for the <code>/v1</code> integration API. The full token is shown once, at mint time.
      </p>

      <form onSubmit={(e) => void handleMint(e)} className="mt-4 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Label</span>
          <input
            className={`${inputCls} w-56`}
            placeholder="e.g. desktop-5090"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Scope</span>
          <select
            className={`${inputCls} w-40`}
            value={scope}
            onChange={(e) => setScope(e.target.value as TokenScope)}
          >
            {SCOPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={minting} className={btnPrimary}>
          {minting ? "Minting..." : "Mint token"}
        </button>
      </form>
      <p className="mt-1 text-xs text-gray-400">{SCOPES.find((s) => s.value === scope)?.hint}</p>

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {justMinted && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950">
          <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
            Copy this token now - it will not be shown again.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded bg-white px-2 py-1 text-xs text-gray-900 dark:bg-gray-900 dark:text-gray-100">
              {justMinted.token}
            </code>
            <button type="button" onClick={() => void copyToken()} className={btnPrimary}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {loading && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>}

      {!loading && (
        <div className="mt-4 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-800">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Scope</th>
                <th className="px-3 py-2">Prefix</th>
                <th className="px-3 py-2">Last used</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {tokens.map((t) => {
                const status = statusOf(t);
                return (
                  <tr key={t.tokenId}>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{t.label}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{t.scope}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500 dark:text-gray-400">{t.tokenPrefix}...</td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtDate(t.lastUsedAt)}</td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmtDate(t.expiresAt)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          status === "active"
                            ? "text-green-600 dark:text-green-400"
                            : "text-gray-400 dark:text-gray-500"
                        }
                      >
                        {status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {status !== "revoked" && (
                        <button
                          type="button"
                          onClick={() => void handleRevoke(t.tokenId)}
                          className="text-xs text-red-600 hover:underline dark:text-red-400"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tokens.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No tokens yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
